// O que a tela diz sobre um envio de assinatura que falhou.
//
// Régua ÚNICA das duas telas que assinam (expediente e venda) — e pura de
// propósito, sem antd, porque o jest do CRA5 não carrega antd v6 e uma regra
// que só existe dentro do JSX não tem como ser testada.
//
// O pedido do Lucas em 30/09/2026 foi "avise que a API está no limite e tente
// mandar de novo sozinho". A parte da TELA é dizer as três coisas que faltavam
// naquele dia: por que não saiu, que o sistema vai tentar sozinho (e quando), e
// que reenviar na mão pode PIORAR — quando o documento já está no cofre da D4
// Sign, o botão "Tentar de novo" sobe uma segunda via assinável do mesmo
// ofício. Foi assim que o OF-2026-003 ficou com duas.
import type { Envelope } from './tipos';

export interface AvisoEnvio {
  tipo: 'warning' | 'error' | 'info';
  titulo: string;
  descricao: string;
  /** O que a tela pode oferecer. Excludentes de propósito: com o documento no
   *  cofre, "enviar de novo" é o clique errado — o servidor recusa com 409. */
  acao: 'reenviar' | 'retomar' | 'cancelar_ou_esperar' | 'nenhuma';
}

const hora = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

export function avisoDoEnvio(envelope: Envelope | null | undefined): AvisoEnvio | null {
  if (!envelope || envelope.status !== 'erro_envio') return null;
  const r = envelope.retentativa || null;
  const motivo = envelope.falha?.texto || 'Veja a trilha abaixo para o motivo.';
  // Do DOCUMENTO: existe via assinável no cofre, deste envelope ou de um
  // irmão mais antigo. Do ENVELOPE: a via é deste, e por isso ele pode ser
  // retomado. A diferença é o que tira o documento do beco sem saída.
  const noCofre = !!r?.no_cofre;
  const esteNoCofre = !!r?.este_no_cofre;

  // Ainda vai tentar sozinho. O texto NÃO pode sugerir reenviar: na cota, cada
  // tentativa consome o mesmo limite que falta para o documento sair.
  if (r?.prevista_em) {
    const h = hora(r.prevista_em);
    return {
      tipo: envelope.falha?.cota ? 'warning' : 'info',
      titulo: envelope.falha?.cota
        ? 'Limite da D4 Sign atingido — o sistema vai tentar de novo sozinho'
        : 'O envio falhou — o sistema vai tentar de novo sozinho',
      descricao: `${motivo} Próxima tentativa automática`
        + (h ? ` por volta das ${h}` : ' em alguns minutos')
        + (r.tentativas > 0 ? ` (${r.tentativas}ª de ${r.maximo} já feitas).` : '.')
        + ' Você não precisa fazer nada.'
        + (noCofre
          ? ' O documento já está no cofre da D4 Sign: falta só avisar os signatários,'
            + ' e reenviar na mão criaria uma segunda via assinável.'
          : ''),
      acao: noCofre ? 'cancelar_ou_esperar' : 'nenhuma',
    };
  }

  // Parou de tentar. Aqui a bola é de uma pessoa — e o que ela pode fazer
  // depende de o documento estar ou não no cofre.
  const esgotou = !!r && r.tentativas >= r.maximo;
  if (noCofre) {
    // Só o envelope QUE SUBIU pode ser retomado — e só se soubermos o passo.
    const podeRetomar = esteNoCofre
      && (r?.etapa === 'createlist' || r?.etapa === 'sendtosigner');
    return {
      tipo: 'error',
      titulo: esgotou
        ? 'O documento NÃO foi enviado, mesmo depois das tentativas automáticas'
        : 'O documento NÃO foi enviado para assinatura',
      descricao: `${motivo} O documento já está no cofre da D4 Sign — enviar de novo criaria`
        + ' uma SEGUNDA via assinável do mesmo documento.'
        + (podeRetomar
          ? ' Use "Retomar envio" para concluir o que faltou, ou cancele este envio'
            + ' para mandar outro.'
          : !esteNoCofre
            ? ' A via é de uma tentativa anterior a esta: cancele a via do cofre'
              + ' e depois mande de novo.'
            : ' Não dá para retomar com segurança (não sei em que passo parou):'
              + ' cancele este envio e mande de novo.'),
      acao: podeRetomar ? 'retomar' : 'cancelar_ou_esperar',
    };
  }
  return {
    tipo: 'error',
    titulo: esgotou
      ? 'O envio falhou, mesmo depois das tentativas automáticas'
      : 'O envio falhou',
    descricao: `${motivo} Nada chegou à D4 Sign — pode enviar de novo.`,
    acao: 'reenviar',
  };
}
